import * as React from "react";
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { drawerTableWrapperClassName } from "../Table/TableStyles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import JobsRepository from "../../../repositories/JobsRepository";
import ConfirmFilesDeletionModal, {
  CONFIRM_FILES_DELETION_CONFIGS,
} from "../ConfirmFilesDeletionModal";
import { QUERY_KEYS } from "../../../constants/QueryKeys";

interface DeletableFile {
  id: string;
  filePath: string;
}

interface FileTypeConfig<T extends DeletableFile> {
  confirmFilesDeletionConfig: (typeof CONFIRM_FILES_DELETION_CONFIGS)[keyof typeof CONFIRM_FILES_DELETION_CONFIGS];
  deletionMethod: (
    jobsRepository: JobsRepository,
    organizationId: string,
    entityId: string,
    fileIds: string[],
  ) => Promise<void>;
  invalidationQueries: (
    organizationId: string,
    entityId: string,
  ) => string[][];
  /** Used in success/error toasts; e.g. "case files". */
  fileTypeLabel: string;
}

interface CreateDeleteFilesRequestTableProps<T extends DeletableFile> {
  organizationId: string;
  entityId: string;
  entityKey: string;
  newFilesForDeletion: T[];
  setNewFilesForDeletion: React.Dispatch<React.SetStateAction<T[]>>;
  fileTypeConfig: FileTypeConfig<T>;
  deleteButtonText?: string;
}

export const CreateDeleteFilesRequestTable = <T extends DeletableFile>({
  organizationId,
  entityId,
  entityKey,
  newFilesForDeletion,
  setNewFilesForDeletion,
  fileTypeConfig,
  deleteButtonText = "Delete files",
}: CreateDeleteFilesRequestTableProps<T>) => {
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] =
    useState<boolean>(false);
  const allRows = [...newFilesForDeletion];
  const newIds = new Set(newFilesForDeletion.map((file) => file.id));
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const getFileName = (filePath: string) => filePath.split("\\").pop();

  const handleConfirmDeletion = async () => {
    const count = newFilesForDeletion.length;
    try {
      await fileTypeConfig.deletionMethod(
        jobsRepository,
        organizationId,
        entityId,
        newFilesForDeletion.map((file) => file.id),
      );
      toast.success(
        `Deletion of ${count} ${fileTypeConfig.fileTypeLabel} started`,
      );
    } catch (error: any) {
      toast.error(
        error?.message ??
          `Failed to start deletion of ${fileTypeConfig.fileTypeLabel}`,
      );
      return;
    }
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
    fileTypeConfig
      .invalidationQueries(organizationId, entityId)
      .forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      });
    setNewFilesForDeletion([]);
    setConfirmDeletionModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-2 pt-2 max-h-[40%] min-h-[40%]">
      <Button
        disabled={!(newFilesForDeletion.length > 0)}
        onClick={() => setConfirmDeletionModalOpen(true)}
      >
        {deleteButtonText}
      </Button>

      <div className={drawerTableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%] min-w-[48px] text-center p-3" />
              <TableHead className="w-[40%] p-3">File Name</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {allRows.map((fileForDeletion) => (
              <TableRow
                key={fileForDeletion.id}
                className={
                  newIds.has(fileForDeletion.id)
                    ? "bg-primary/10"
                    : undefined
                }
              >
                <TableCell className="text-center w-[120px]" />
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm">
                          {getFileName(fileForDeletion.filePath)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {fileForDeletion.filePath}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="p-3 flex justify-end items-center">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    aria-label="Cancel"
                    onClick={() => {
                      setNewFilesForDeletion((prevRows) =>
                        prevRows.filter(
                          (entity) => entity.id !== fileForDeletion.id,
                        ),
                      );
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {confirmDeletionModalOpen && (
        <ConfirmFilesDeletionModal
          open={confirmDeletionModalOpen}
          entityKey={entityKey}
          filePaths={newFilesForDeletion.map((f) => f.filePath)}
          confirmFilesDeletionConfig={fileTypeConfig.confirmFilesDeletionConfig}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={handleConfirmDeletion}
        />
      )}
    </div>
  );
};

const CASE_FILES_CONFIG: FileTypeConfig<any> = {
  confirmFilesDeletionConfig: CONFIRM_FILES_DELETION_CONFIGS.CASE_FILES,
  deletionMethod: (jobsRepository, organizationId, caseId, fileIds) =>
    jobsRepository.startCaseFilesDeletion(organizationId, caseId, fileIds),
  invalidationQueries: (organizationId, caseId) => [
    [QUERY_KEYS.DELETE_CASE_FILES, organizationId, caseId],
    [QUERY_KEYS.CASE_FILES, caseId],
  ],
  fileTypeLabel: "case files",
};

const INTAKE_FILES_CONFIG: FileTypeConfig<any> = {
  confirmFilesDeletionConfig: CONFIRM_FILES_DELETION_CONFIGS.INTAKE_FILES,
  deletionMethod: (jobsRepository, organizationId, intakeId, fileIds) =>
    jobsRepository.startIntakeFilesDeletion(organizationId, intakeId, fileIds),
  invalidationQueries: (organizationId, intakeId) => [
    [QUERY_KEYS.DELETE_INTAKE_FILES, organizationId, intakeId],
    [QUERY_KEYS.INTAKE_FILES, intakeId],
  ],
  fileTypeLabel: "intake files",
};

const PROVIDER_FILES_CONFIG: FileTypeConfig<any> = {
  confirmFilesDeletionConfig: CONFIRM_FILES_DELETION_CONFIGS.PROVIDER_FILES,
  deletionMethod: (jobsRepository, organizationId, providerId, fileIds) =>
    jobsRepository.startProviderFilesDeletion(
      organizationId,
      providerId,
      fileIds,
    ),
  invalidationQueries: (organizationId, providerId) => [
    [QUERY_KEYS.DELETE_PROVIDER_FILES, organizationId, providerId],
    [QUERY_KEYS.PROVIDER_FILES, providerId],
  ],
  fileTypeLabel: "provider files",
};

const TEMPLATE_FILES_CONFIG: FileTypeConfig<any> = {
  confirmFilesDeletionConfig: CONFIRM_FILES_DELETION_CONFIGS.TEMPLATE_FILES,
  deletionMethod: (jobsRepository, organizationId, templateId, fileIds) =>
    jobsRepository.startTemplateFilesDeletion(
      organizationId,
      templateId,
      fileIds,
    ),
  invalidationQueries: (organizationId, templateId) => [
    [QUERY_KEYS.DELETE_TEMPLATE_FILES, organizationId, templateId],
    [QUERY_KEYS.TEMPLATE_FILES, templateId],
  ],
  fileTypeLabel: "template files",
};

const FIRM_DOCUMENTS_CONFIG: FileTypeConfig<any> = {
  confirmFilesDeletionConfig: CONFIRM_FILES_DELETION_CONFIGS.FIRM_DOCUMENTS,
  deletionMethod: (
    jobsRepository,
    organizationId,
    firmDocumentsFolderId,
    fileIds,
  ) =>
    jobsRepository.startFirmDocumentsDeletion(
      organizationId,
      firmDocumentsFolderId,
      fileIds,
    ),
  invalidationQueries: (organizationId, firmDocumentsFolder) => [
    [QUERY_KEYS.DELETE_FIRM_DOCUMENTS, organizationId, firmDocumentsFolder],
    [QUERY_KEYS.FIRM_DOCUMENTS, firmDocumentsFolder],
  ],
  fileTypeLabel: "firm documents",
};

export const CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS = {
  CASE_FILES_CONFIG,
  INTAKE_FILES_CONFIG,
  PROVIDER_FILES_CONFIG,
  TEMPLATE_FILES_CONFIG,
  FIRM_DOCUMENTS_CONFIG,
};
